import { dropdownValueTransform } from "@/Utils/importCommon/common";
import {
  numberValidation,
  requiredFieldValidation,
  timestampValidation,
  validate,
  valueInArrayValidation,
} from "@/Utils/importCommon/validationFunctions";
import { IInputOption } from "@/components/GenericComponents/input";
import {
  TemplateColumn,
  ValidateFunctionProps,
} from "@/components/ImportComponents/types";

import { sortBy } from "lodash";
import type { ACCESS_PARENT } from "server/coreApi/accessParents/types.js";
import type { ACCESS_POINT } from "server/coreApi/accessPoints/types.js";
import type { MinimalCustomer } from "server/coreApi/customers/types.js";

import {
  EVENT_CUSTOMER_NAME_KEY,
  EVENT_TIMESTAMP_KEY,
  EVENT_WASTE_ROOM_NAME_KEY,
  EVENT_WASTE_TYPE_NAME_KEY,
  EVENT_WEIGHT_KEY,
} from "./constants";

export const getEventsColumns = ({
  customers,
  wasteTypes,
  wasteRooms,
}: {
  customers?: MinimalCustomer[];
  wasteTypes?: (ACCESS_POINT & { wasteRoom: ACCESS_PARENT })[];
  wasteRooms?: ACCESS_PARENT[];
}): TemplateColumn[] => [
  {
    key: EVENT_TIMESTAMP_KEY,
    title: "importLabels:eventTimestamp",
    isRequired: true,
    validate: ({ value }: ValidateFunctionProps) =>
      validate([requiredFieldValidation, timestampValidation], { value }),
  },
  {
    key: EVENT_CUSTOMER_NAME_KEY,
    title: "importLabels:customerName",
    isRequired: true,
    isDropdown: true,
    isGenerated: true,
    generatingDependents: [EVENT_CUSTOMER_NAME_KEY],
    getGeneratedValue: (customerName: any): string =>
      customers?.find(
        (c) =>
          c.name?.toLowerCase().replace(/\s/g, "") ===
          customerName?.toLowerCase().replace(/\s/g, ""),
      )?.id || customerName,
    dropdownOptions: sortBy(
      customers?.map(
        ({ id: value, name: option }) => ({ option, value }) as IInputOption,
      ) || [],
      "option",
    ),
    validate: ({ value }: ValidateFunctionProps) =>
      validate([requiredFieldValidation, valueInArrayValidation], {
        value,
        arrayOfValues: customers,
      }),
    transformValue: dropdownValueTransform,
  },
  {
    key: EVENT_WASTE_ROOM_NAME_KEY,
    title: "importLabels:wasteRoomName",
    isRequired: true,
    isDropdown: true,
    isGenerated: true,
    generatingDependents: [EVENT_WASTE_ROOM_NAME_KEY],
    dropdownOptions:
      wasteRooms?.map(
        ({ id: value, name: option }) => ({ option, value }) as IInputOption,
      ) || [],
    validate: ({ value }: ValidateFunctionProps) =>
      validate([requiredFieldValidation, valueInArrayValidation], {
        value,
        arrayOfValues: wasteRooms,
      }),
    getGeneratedValue: (wasteRoomName: any): string =>
      wasteRooms?.find(
        (c) =>
          c.name?.toLowerCase().replace(/\s/g, "") ===
          wasteRoomName?.toLowerCase().replace(/\s/g, ""),
      )?.id || wasteRoomName,
    transformValue: dropdownValueTransform,
  },
  {
    key: EVENT_WASTE_TYPE_NAME_KEY,
    title: "importLabels:wasteStreamName",
    isRequired: true,
    validate: ({
      value,
      linkingValues: validWasteTypes,
    }: ValidateFunctionProps) =>
      validate([requiredFieldValidation, valueInArrayValidation], {
        value,
        arrayOfValues: validWasteTypes?.map((id) => ({ id })),
      }),
    isDropdown: true,
    dropdownOptions: wasteTypes?.map((w) => ({
      option: w.name || "",
      value: w.id,
      filterParamId: w.wasteRoom.id,
    })),
    filterBy: EVENT_WASTE_ROOM_NAME_KEY,
    transformValue: dropdownValueTransform,
  },
  {
    key: EVENT_WEIGHT_KEY,
    title: "formLabels:weight",
    isRequired: true,
    validate: ({ value }: ValidateFunctionProps) =>
      validate([requiredFieldValidation, numberValidation], { value }),
  },
];
